/**
 * Phase 21.1 / Phase 22 — Shared always-visible payment badge.
 *
 * Cash variants are deliberately VIVID (amber) so on-site collection is
 * impossible to miss; invoiced variants (vat/rct) are neutral gray. The
 * badge is rendered independently of any configurable card-field list —
 * managers cannot accidentally hide it.
 *
 * Code matching is intentionally lenient: tenants can rename payment types
 * in Settings, and the seeded codes (`simple_cash`, `cash_with_invoice`)
 * differ from the colloquial shorthand the brief uses (`cash`, `cash_invoice`).
 * We map by the underlying *code shape*, not the display label.
 *
 * Sizes:
 *   - `sm`      — list rows, staff cards, and any place wide enough for the
 *                 full "CASH — COLLECT" / "VAT — Invoiced" label.
 *   - `compact` — Phase 39: 7-day calendar tiles. Icon + 3–4 letter
 *                 abbreviation ("CASH", "C+I", "VAT", "RCT") that fits the
 *                 narrow column at 1024-1920px. Full label remains in the
 *                 native tooltip + `aria-label`.
 *   - `lg`      — expanded staff card body, booking-details modal/panel.
 *   - `icon`    — minimum-footprint icon-only pill for tight calendar grids
 *                 (monthly view).
 */
import { Wallet, Receipt } from "lucide-react";

export function paymentBadgeVariant(job) {
  const code = (job?.payment_type || "").toString().toLowerCase().trim();
  const label = (job?.payment_type_name || job?.payment_type_label || "").toString();
  // Simple Cash — no invoice, on-site collection required.
  if (code === "simple_cash" || code === "cash" || code === "simple") {
    return {
      kind: "cash_collect",
      text: "CASH — COLLECT",
      shortText: "CASH",
      Icon: Wallet,
      // High contrast amber; works against both light and slightly darker card bg.
      cls: "bg-amber-400 text-amber-950 border-amber-600",
    };
  }
  // Cash with invoice — still collection required, slightly softer label.
  if (code === "cash_with_invoice" || code === "cash_invoice" || code.includes("cash")) {
    return {
      kind: "cash_invoice",
      text: "CASH (Invoice)",
      shortText: "C+I",
      Icon: Wallet,
      cls: "bg-amber-300 text-amber-950 border-amber-500",
    };
  }
  if (code === "vat") {
    return {
      kind: "vat",
      text: "VAT — Invoiced",
      shortText: "VAT",
      Icon: Receipt,
      cls: "bg-muted text-muted-foreground border-border",
    };
  }
  if (code === "rct") {
    return {
      kind: "rct",
      text: "RCT — Invoiced",
      shortText: "RCT",
      Icon: Receipt,
      cls: "bg-muted text-muted-foreground border-border",
    };
  }
  // Unknown / custom code → neutral gray with humanised text.
  const humanised = (label || code || "Payment")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return {
    kind: "other",
    text: humanised,
    shortText: humanised.slice(0, 4).toUpperCase(),
    Icon: Receipt,
    cls: "bg-muted text-muted-foreground border-border",
  };
}

export default function PaymentBadge({ job, size = "sm", className = "" }) {
  const v = paymentBadgeVariant(job);
  const Icon = v.Icon;

  let padding;
  let iconSize;
  let body;

  if (size === "icon") {
    padding = "px-1.5 py-0.5";
    iconSize = "h-3 w-3";
    body = null; // icon only
  } else if (size === "compact") {
    // Phase 39 — narrow 7-day-tile variant. Same height as `sm` but uses
    // the 3-4 letter `shortText` so even the longest column (~120px at
    // 1024px) never overflows. Tooltip keeps the full label discoverable.
    padding = "px-1.5 py-0.5 text-[10px]";
    iconSize = "h-3 w-3";
    body = v.shortText;
  } else if (size === "lg") {
    padding = "px-3 py-1.5 text-sm";
    iconSize = "h-4 w-4";
    body = v.text;
  } else {
    padding = "px-2 py-0.5 text-[11px]";
    iconSize = "h-3 w-3";
    body = v.text;
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border font-extrabold uppercase tracking-wider whitespace-nowrap max-w-full ${padding} ${v.cls} ${className}`.trim()}
      data-testid={`payment-badge-${job.id}`}
      data-payment-kind={v.kind}
      aria-label={`Payment method: ${v.text}`}
      title={v.text}
    >
      <Icon className={`shrink-0 ${iconSize}`} aria-hidden="true" />
      {body}
    </span>
  );
}
